<script setup lang="ts">
import EntityCustomerSelect from '@/components/asicw/common/EntityCustomerSelect.vue'
import CopyButton from '@/components/common/CopyButton.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { useClaimInvoices } from '@/composables/asicw/claim-invoices'
import { getCustomerDisplayName } from '@/helpers/core/customers'
import { NotifyFail } from '@/helpers/core/notifications'
import { dateFormatter } from '@/helpers/ep/formatters'
import { exportClaimInvoiceCSV } from '@/services/asicw/claim-invoices.service'
import type { IClaimInvoice } from '@/types/asicw/invoices'
import { InvoiceStatus, InvoiceType, type IClaimInvoiceFilters } from '@/types/asicw/invoices'
import type { ICustomer } from '@/types/core/Customer'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadFile, getCostColor, getCssVarValue, hex2rgba } from '@/util'
import { formatCurrency, tableColumnCurrency } from '@/util/formatters.ts'
import { Icon } from '@iconify/vue'
import { watchDebounced } from '@vueuse/core'
import { DateTime } from 'luxon'
import { vMaska } from 'maska/vue'
import { reactive, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

// Initialize filters with route query parameters to avoid double query execution
const filters = reactive<IClaimInvoiceFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  id: route.query.id ? parseInt(route.query.id as string) : undefined,
  period: undefined,
  status: undefined,
  invoiced_to: undefined,
  customer_id: undefined,
  claim_number: undefined,
})

const claimNumberInput = ref<string>()
const invoicedToInput = ref<string>()
const invoiceIDInput = ref<string>(route.query.id?.toString() ?? '')

// Status editing state
const editingStatus = ref<Record<number, boolean>>({})
const selectedStatus = ref<Record<number, InvoiceStatus | undefined>>({})

// Status color mapping similar to priority color mapping
const statusColorMap: Record<string, string> = {
  [InvoiceStatus.READY_TO_SEND]: 'var(--claim-invoice-status-ready-to-send)',
  [InvoiceStatus.SENT]: 'var(--claim-invoice-status-sent)',
  [InvoiceStatus.PAYMENT_LATE]: 'var(--claim-invoice-status-payment-late)',
  [InvoiceStatus.PAID]: 'var(--claim-invoice-status-paid)',
}

// Helper functions for status display
const getStatusColor = (status: string): string => {
  return statusColorMap[status] || 'var(--status-default)'
}

const getStatusTagColor = (status: string): string | undefined => {
  const colorVar = getStatusColor(status)
  const colorValue = getCssVarValue(colorVar)
  if (!colorValue) return undefined
  try {
    return hex2rgba(colorValue, 0.2)
  } catch {
    return undefined
  }
}

// Vue Query
const { getClaimInvoicesQuery, deleteClaimInvoiceMutation, updateClaimInvoiceMutation, refreshClaimInvoiceMutation } =
  useClaimInvoices()
const { isFetching, isError, isRefetching, data, error, refetch } = getClaimInvoicesQuery(filters)

watchDebounced(
  claimNumberInput,
  (value) => {
    filters.claim_number = value || undefined
  },
  { debounce: 500 }
)

watchDebounced(
  invoiceIDInput,
  (value) => {
    filters.id = value ? parseInt(value) : undefined
  },
  { debounce: 500 }
)

const handleEntityChange = ({ entity, customer }: { entity: string; customer?: ICustomer }) => {
  filters.invoiced_to = entity
  filters.customer_id = customer?.id
  invoicedToInput.value = customer ? getCustomerDisplayName(customer) : entity
}

const handleDelete = (invoiceID: number, e: MouseEvent) => {
  e.stopPropagation()
  DeleteConfirmationModal(
    'Delete Invoice',
    'Are you sure you want to delete this invoice? <b>All other invoices associated with any of the claims from this invoice will also be deleted.</b>',
    invoiceID,
    deleteClaimInvoiceMutation.mutateAsync
  )
}

const handleRefresh = (invoiceID: number, e: MouseEvent) => {
  e.stopPropagation()
  refreshClaimInvoiceMutation.mutate(invoiceID)
}

const handleExport = async (invoice: IClaimInvoice) => {
  try {
    downloadFile(await exportClaimInvoiceCSV(invoice.id), `invoice_${invoice.id}.csv`)
  } catch (err: any) {
    NotifyFail(`Failed to download invoice: ${err.message}`)
  }
}

// Status editing handlers
const startEditingStatus = (invoiceId: number, currentStatus?: InvoiceStatus) => {
  editingStatus.value[invoiceId] = true
  selectedStatus.value[invoiceId] = currentStatus
}

const cancelEditingStatus = (invoiceId: number) => {
  editingStatus.value[invoiceId] = false
  selectedStatus.value[invoiceId] = undefined
}

const onStatusChange = (invoiceId: number, statusValue?: InvoiceStatus) => {
  const form = { status: statusValue }
  updateClaimInvoiceMutation.mutate(
    { id: invoiceId, data: form },
    {
      onSettled: () => {
        editingStatus.value[invoiceId] = false
      },
    }
  )
}

const getSummaries = (param: { columns: any[]; data: any[] }) => {
  const { columns, data } = param
  const sums: string[] = []
  columns.forEach((column, index) => {
    if (column.property === 'policy_group_number') {
      sums[index] = 'Total Cost'
    } else if (column.property === 'cost') {
      const values = data.map((item) => parseFloat(item[column.property]) || 0)
      const total = values.reduce((prev, curr) => prev + curr, 0)
      sums[index] = tableColumnCurrency({}, {}, total)
    } else {
      sums[index] = ''
    }
  })
  return sums
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex justify-between mb-4">
      <div class="flex gap-2 items-start flex-wrap max-h-20 overflow-hidden">
        <el-input
          v-model="invoiceIDInput"
          v-maska
          data-maska="9"
          data-maska-tokens="9:[0-9]:repeated"
          placeholder="Invoice #"
          clearable
          style="width: 100px"
          :disabled="isFetching"
        />
        <el-date-picker
          v-model="filters.period"
          type="month"
          placeholder="Period"
          format="MMMM YYYY"
          value-format="YYYY-MM-DD"
          style="width: 200px"
          :disabled="isFetching"
        />
        <el-select v-model="filters.status" placeholder="Status" clearable style="width: 200px" :disabled="isFetching">
          <el-option v-for="status in Object.values(InvoiceStatus)" :key="status" :label="status" :value="status" />
        </el-select>
        <EntityCustomerSelect
          v-model="invoicedToInput"
          placeholder="Invoiced To"
          style="width: 200px"
          :disabled="isFetching"
          @change="handleEntityChange"
        />
        <el-input
          v-model="claimNumberInput"
          placeholder="Claim Number"
          clearable
          style="width: 200px"
          :disabled="isFetching"
        />
      </div>
    </div>
    <el-table v-loading="isFetching" :data="data?.data" empty-text="No data" stripe>
      <el-table-column type="expand" width="50">
        <template #default="scope">
          <div>
            <el-collapse v-if="Object.keys(scope.row.items).length > 0">
              <el-collapse-item v-for="(items, key) in scope.row.items" :key="key" :name="key">
                <template #title>
                  <div class="flex items-center justify-center w-full h-full text-sm">{{ key }}</div>
                </template>
                <el-table :data="items" size="small" show-summary :summary-method="getSummaries">
                  <el-table-column label="Items" header-align="center">
                    <el-table-column prop="policy_group_number" label="Policy Group Number" :min-width="40" />
                    <el-table-column
                      prop="claim_date_of_loss"
                      label="Date of Loss"
                      :min-width="30"
                      :formatter="dateFormatter"
                    />
                    <el-table-column prop="claim_number" label="Claim Number" :min-width="30" />
                    <el-table-column prop="charge" label="Charge" :min-width="50" />
                    <el-table-column prop="cost" label="Cost" :min-width="30">
                      <template #default="scope">
                        <span :style="{ color: getCostColor(scope.row.cost) }">
                          {{ formatCurrency(parseFloat(scope.row.cost)) }}
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="payable_from" label="Payable From" :min-width="80" />
                    <el-table-column prop="payable_to" label="Payable To" :min-width="80" />
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
            <div v-else class="text-center text-gray-500 py-4">No items available</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="id" label="Invoice #" :min-width="80" />
      <el-table-column label="Period" :min-width="100">
        <template #default="scope">
          <div class="flex items-center gap-1">
            <span>{{ DateTime.fromObject({ month: scope.row.month }).toFormat('MMMM') }} {{ scope.row.year }}</span>
            <el-tooltip v-if="scope.row.type !== InvoiceType.CLAIM_PAYMENT" placement="top">
              <template #content>
                <div>
                  <div>Issued: {{ DateTime.fromISO(scope.row.issue_date).toFormat('MMM dd, yyyy') }}</div>
                  <div>Due: {{ DateTime.fromISO(scope.row.due_date).toFormat('MMM dd, yyyy') }}</div>
                </div>
              </template>
              <Icon icon="material-symbols:help-outline" :height="20" class="cursor-help" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Invoiced To" :min-width="140">
        <template #default="scope">
          {{ scope.row.customer ?? scope.row.invoiced_to }}
        </template>
      </el-table-column>
      <el-table-column prop="type" label="Type" :min-width="100" />
      <el-table-column prop="amount" label="Amount" :min-width="100">
        <template #default="scope">
          <span :style="{ color: getCostColor(scope.row.amount) }">
            {{ formatCurrency(parseFloat(scope.row.amount)) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="Status" :min-width="160">
        <template #default="scope">
          <!-- Displaying -->
          <div v-if="!editingStatus[scope.row.id]" class="flex gap-2">
            <el-tag
              v-if="scope.row.status"
              disable-transitions
              :color="getStatusTagColor(scope.row.status)"
              class="inline-flex gap-1 cursor-pointer"
              style="border: none"
              @click="startEditingStatus(scope.row.id, scope.row.status)"
            >
              <span :style="{ color: getStatusColor(scope.row.status) }">
                {{ scope.row.status }}
              </span>
              <Icon
                :color="getStatusColor(scope.row.status)"
                icon="material-symbols:keyboard-arrow-down-rounded"
                :height="16"
              />
            </el-tag>
            <el-tag
              v-else
              disable-transitions
              color="var(--el-fill-color-lighter)"
              class="inline-flex gap-1 cursor-pointer"
              style="border: 1px dashed var(--el-border-color); color: var(--el-text-color-placeholder)"
              @click="startEditingStatus(scope.row.id, scope.row.status)"
            >
              <span>Status</span>
              <Icon
                color="var(--el-text-color-placeholder)"
                icon="material-symbols:keyboard-arrow-down-rounded"
                :height="16"
              />
            </el-tag>
          </div>
          <!-- Editing -->
          <span v-else class="text-nowrap flex gap-2">
            <el-select
              :model-value="selectedStatus[scope.row.id]"
              :min-width="160"
              :disabled="updateClaimInvoiceMutation.isPending.value"
              :loading="updateClaimInvoiceMutation.isPending.value"
              clearable
              @click.stop
              @change="(val) => onStatusChange(scope.row.id, val)"
            >
              <el-option v-for="opt in Object.values(InvoiceStatus)" :key="opt" :label="opt" :value="opt" />
            </el-select>

            <el-button
              :disabled="updateClaimInvoiceMutation.isPending.value"
              link
              @click.stop="cancelEditingStatus(scope.row.id)"
            >
              <Icon icon="material-symbols:close" :height="16" />
            </el-button>
          </span>
        </template>
      </el-table-column>
      <el-table-column label="Associated Claims" :min-width="184">
        <template #default="scope">
          <div class="flex flex-wrap gap-1">
            <CopyButton v-for="claim in scope.row.associated_claims" :key="claim" :value="claim">
              <el-tag size="small">
                {{ claim }}
              </el-tag>
            </CopyButton>
          </div>
        </template>
      </el-table-column>
      <el-table-column :width="100" align="right" fixed="right">
        <template #default="scope">
          <div class="flex justify-end">
            <el-button
              :disabled="scope.row.status === InvoiceStatus.PAID"
              link
              title="Refresh"
              @click="handleRefresh(scope.row.id, $event)"
            >
              <Icon icon="material-symbols:refresh-rounded" :height="16" />
            </el-button>
            <el-button link title="View Invoice" @click="handleExport(scope.row as IClaimInvoice)">
              <Icon icon="material-symbols:download" :height="16" />
            </el-button>
            <el-button
              :disabled="scope.row.status === InvoiceStatus.PAID"
              link
              title="Delete"
              @click="handleDelete(scope.row.id, $event)"
            >
              <Icon icon="material-symbols:delete-outline" :height="16" />
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="self-end mt-4"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
  </template>
</template>

<style scoped lang="scss">
:deep(.el-tag__content) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
